import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useSelector, useDispatch, useStore } from 'react-redux';
import { v4 as v4uuid } from 'uuid';
import merge from 'lodash.merge';
import ContainerTemplate from '../../components/ContainerTemplate';
import UnknownComponent, { isUnknownComponent } from '../../components/UnknownComponent';
import Checkbox from '../../components/Checkbox';
import Image from '../../components/Image';
import Input from '../../components/Input';
import Radio from '../../components/Radio';
import Select from '../../components/Select';
import Separator from '../../components/Separator';
import Switcher from '../../components/Switcher';
import Chart from '../../components/Chart';
import Text from '../../components/Text';
import { pageSuccess } from '../../store/page/actions';
import useTrackRouteChangeHook from '../../utils/trackRouteChangeHook';
import useScrollToTopHook from '../../utils/scrollToTopHook';
import { 
  setItem as setLocalCacheRaw,
 } from '../../utils/indexedDbHelper';
import { analyzeAndPrefetchImages } from '../../utils/prefetchHelper';
import { preloadImages } from '../../utils/preloadHelper';
import generateComponentStyles from '../../utils/styleHelper';
import { resolveRootLayout } from '../../utils/rootLayout';
import { attachScriptsToHeader, attachFontsToHeader, replacePlaceholdersWithVariables,
  formShimmerCacheKey, transformShimmerCache, parseOldFormat, parseNewFormat, updateDataWithModeUpdate, sessionDataTransformer, mergeVariableBags, normalizeScreenSettings, migrateSchemeNode } from '../../utils/base';
import parseJson from '../../utils/json';
import { ApplicationState } from '../../store';
import { getGlobalVariables } from '../../utils/globalVariableHelper';
import { WebSocketFactory, WebSocketProvider, WebSocketMessage } from '../../services/websocket';
import SessionHelper from '../../utils/sessionHelper';
import DeviceHelper from '../../utils/deviceHelper';
import { setFrameApiContext } from '../../utils/req';

import * as styles from './page.module.scss';
import { NodeTypeScheme } from '../../types/base';
import { toCacheableBody } from '../../utils/designSystem';
import { normalizeResponseBody } from '../../utils/normalizeAppData';

// FE-4577 — see the note in `store/page/sagas.ts`: cached bodies must not carry
// the design system, which the trust boundary now merges into every payload.
const setLocalCache = (key: string, body: any) => setLocalCacheRaw(key, toCacheableBody(body));

const styleElementId = 'custom-css-header-kdlshHhgjhgf';

const componentMapping = {
  checkbox: Checkbox,
  image: Image,
  input: Input,
  radio: Radio,
  select: Select,
  separator: Separator,
  switcher: Switcher,
  chart: Chart,
  text: Text,
};

const Page: React.FC = () => {
  const dispatch = useDispatch();
  const store = useStore<ApplicationState>();
  const [fontsLoaded, setFontsLoaded] = useState(false);
  const [customFontLoaded, setCustomFontLoaded] = useState(false);
  const [lastFrameEvent, setLastFrameEvent] = useState<MessageEvent | null>(null);
  // Guards the one-shot frame handshake — see the receiveMessage handler below.
  const frameSchemaAppliedRef = useRef(false);
  const socketRef = useRef<WebSocketProvider | null>(null);
  const pageWrapRef = useRef<HTMLDivElement>(null);
  const config = useSelector((state: ApplicationState) => state.config);
  const page = useSelector((state: ApplicationState) => state.page);
  const { data } = config || {};
  const { features } = data || {};
  const { editor: editorUrl } = (data as any)?.urls || {};

  // Detected once — true when the iframe loads with ?frame=true query param
  const isFrameMode = new URLSearchParams(window.location.search).has('frame');
  const isProcesses = features?.processes || false;
  const { data: pageData2, settings, variableData, session_data, styleChunks, screenId } = page || {};
  const { deviceId, sessionId, hash, localShimmer } = session_data || {};
  
  // Get global variables from localStorage and assign to 'c' key
  const globalVariables = getGlobalVariables();
  // mergeVariableBags (not a plain merge): keeps the resolved `settings.variables`
  // objects from being clobbered by the unresolved `"{{parameters.X}}"` strings
  // that arrive in `variableData`, so dynamic content (table `source`, docs lists)
  // renders on first paint instead of only after an UPDATE_VARIABLE_SUCCESS.
  const mergedVariables = mergeVariableBags(
    (settings?.variables || {}),
    variableData || {},
    globalVariables || {},
  );
  
  const pageData = replacePlaceholdersWithVariables(pageData2, mergedVariables);
  const newSettings = { ...settings, variables: mergedVariables };
  const { page: ppRaw } = pageData || {};

  // Root layout (legacy `__flex_root__` unwrap, flex/grid wrapper, top-level `parentStyle`)
  // — shared with the editor's view-mode frame so the two can't drift. See `rootLayout.ts`.
  const {
    items: pp, pageBackgroundStyle, wrapStyle, isColumnRoot, topLayerParentStyle,
  } = resolveRootLayout(ppRaw, settings);

  useTrackRouteChangeHook(hash);
  // FE-4866: `.pageWrap` is what scrolls on a published page (the document doesn't), and
  // Page is never remounted between screens — so without this its offset survives every
  // navigation. Same hash contract as the containers: the sagas send a fresh hash once per
  // navigation and `skip-` for everything else.
  useScrollToTopHook(pageWrapRef, hash);

  // Frame mode: notify parent that iframe is ready to receive the initial schema.
  // Fires on mount (sends to '*' as fallback) and again once editorUrl is resolved from config.
  useEffect(() => {
    if (!isFrameMode || !window.parent) return;
    window.parent.postMessage({ type: 'iframeReady' }, editorUrl || '*');
  }, [isFrameMode, editorUrl]);

  // Frame mode: receive the initial schema from the parent editor via postMessage.
  // After this first handshake, all subsequent navigation works through the normal saga/API flow.
  useEffect(() => {
    if (!isFrameMode) return;

    const receiveMessage = (event: MessageEvent) => {
      const isAllowed = editorUrl
        ? event.origin === editorUrl
        : event.source === window.parent;

      if (!isAllowed || !event.source) return;

      const result = parseJson(event.data);
      const sourceWindow = event.source as Window;

      if (result.success && (result as any)?.data?.page) {
        // Apply the posted schema ONCE per document. The parent answers every
        // `iframeReady`, and we announce twice (on mount, then again once `editorUrl`
        // resolves from the async config fetch) — so the schema arrives twice. Because
        // PAGE_SUCCESS resets `variableData` wholesale, a re-apply throws away whatever
        // the screen's `on_launch` handlers already wrote, and those handlers don't
        // re-run (Container latches `launchExecutedRef`): the preview would render
        // correctly and then break a moment later. Both posts carry the same schema,
        // so ignoring the later one loses nothing.
        if (frameSchemaAppliedRef.current) {
          (event.source as Window).postMessage('Parsed JSON', event.origin);
          return;
        }
        frameSchemaAppliedRef.current = true;
        const incoming = (result as any).data;
        setFrameApiContext({
          companyShortname: incoming.company_shortname,
          projectShortname: incoming.project_shortname,
        });
        setLastFrameEvent(event);
        dispatch(pageSuccess({
          // Same reason as `settings` below: the posted schema never passed through
          // parseOldFormat/parseNewFormat, so the protocol migration has to run here.
          // It is what turns the legacy camelCase `activeState` into the `active_state`
          // components read — without it NO node state is applied in view mode, so a
          // constructed component (Calendar / Select / Table / Choice) shows only its
          // base look: placeholder hidden, dropdown never opening.
          data: migrateSchemeNode(incoming.page),
          // The editor posts its own working config, which never passed through
          // parseOldFormat/parseNewFormat — normalize it so view mode boots with the same
          // settings shape (parsed `variables` bag, registered custom resolutions) the API
          // path produces. Without this the screen renders with NO variables.
          settings: normalizeScreenSettings(incoming.settings),
          session_data: sessionDataTransformer({
            deviceId: DeviceHelper.getOrCreateDeviceId(),
            sessionId: SessionHelper.getOrCreateSessionId(),
            ...(incoming.session_data || {}),
          }),
          variableData: incoming.variableData,
          screenId: incoming.screenId,
        }));
        sourceWindow.postMessage('Parsed JSON', event.origin);
      } else {
        sourceWindow.postMessage('Failed to parse JSON', event.origin);
      }
    };

    window.addEventListener('message', receiveMessage);
    return () => window.removeEventListener('message', receiveMessage);
  }, [isFrameMode, editorUrl, dispatch]);

  // Frame mode: report rendered height back to the parent so it can resize the iframe.
  useEffect(() => {
    if (!isFrameMode || !lastFrameEvent || !pp) return;
    const timer = setTimeout(() => {
      const elementId = '6201:298088';
      const element = document.getElementById(elementId);
      if (element) {
        const sourceWindow = lastFrameEvent.source as Window;
        sourceWindow.postMessage({ newHeight: element.scrollHeight }, lastFrameEvent.origin);
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [isFrameMode, pp, lastFrameEvent]);

  // Frame mode: bridge the "A" hotkey to the parent editor. Keydowns inside the iframe
  // don't bubble to the parent window, so we forward a toggle-preview message instead of
  // trying to handle the click here. Passive listener — never preventDefault/stopPropagation.
  useEffect(() => {
    if (!isFrameMode || !window.parent) return;

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.keyCode !== 65) return;
      if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;

      const el = document.activeElement as HTMLElement | null;
      const tag = el?.tagName?.toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || el?.isContentEditable) return;

      window.parent.postMessage({ type: 'toggle-preview' }, editorUrl || '*');
    };

    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [isFrameMode, editorUrl]);

  useEffect(() => {
    const { basic, variables } = data || {};

    // The theme is injected on its own. This used to read
    // `if (basic && variables && classes)` around a builder that turned a global
    // `classes` blob into CSS rules — so whenever that blob was absent the
    // <style> element was never created at all, and every `$token -> var(--token)`
    // reference in every component silently resolved to nothing.
    //
    // Style classes are branch-scoped resources now (`/api/v1/classes`); the blob
    // is gone, and the theme no longer depends on it.
    if (!basic || !variables) return;

    let styleElement = document.getElementById(styleElementId);
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleElementId;
      document.head.insertBefore(styleElement, document.head.firstChild);
    }
    styleElement.innerText = `${variables}\n${basic}`;
  }, [data]);

  useEffect(() => {
    if (settings?.script) attachScriptsToHeader(settings?.script);
  }, [settings?.script]);

  useEffect(() => {
    if (!settings?.fonts) {
      // setFontsLoaded(true);
    };
    if (settings?.fonts) {
      attachFontsToHeader(settings?.fonts).then(() => {
       setTimeout(() => {
        // setFontsLoaded(true);
       }, 0); 
      }).catch((error) => {
        console.error('Font loading failed:', error);
        // setFontsLoaded(true); 
        // Fallback to show content even if fonts fail to load
      });
    } else {
      // setFontsLoaded(true); 
      // No fonts to load, show content immediately
    }
  }, [settings?.fonts]);

  useEffect(() => {
    if (settings?.page) {
      const {
        title, description, icon, theme_color: themeColor,
      } = settings.page;
      if (title) {
        document.title = title;
      }
      if (description) {
        let metaDescription = document.querySelector("meta[name='description']") as HTMLMetaElement;
        if (!metaDescription) {
          metaDescription = document.createElement('meta');
          metaDescription.name = 'description';
          metaDescription.content = description;
          document.getElementsByTagName('head')[0].appendChild(metaDescription);
        }
      }
      if (icon) {
        let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.getElementsByTagName('head')[0].appendChild(link);
        }
        link.href = icon;
      }
      // TNBEWEB-1424: drive the mobile status bar color from the screen's
      // `settings.page.theme_color`. Create-and-update so live `pageUpdate`
      // changes take effect; remove the tag when the color is cleared.
      let themeMeta = document.querySelector("meta[name='theme-color']") as HTMLMetaElement | null;
      if (themeColor) {
        if (!themeMeta) {
          themeMeta = document.createElement('meta');
          themeMeta.name = 'theme-color';
          document.getElementsByTagName('head')[0].appendChild(themeMeta);
        }
        themeMeta.content = themeColor;
      } else if (themeMeta) {
        themeMeta.remove();
      }
    }
  }, [settings]);

  const handlePageUpdate = useCallback((message: WebSocketMessage) => {
    if (message.type === 'pageUpdate' && message.data) {
      // FE-4938 — the provider keeps the callback it was given at connect time,
      // so a `page` captured from render is the page as it was THEN, and each
      // frame would be merged into it and discard the previous frame (and any
      // variable written since). Read the store as it is when the frame lands.
      const livePage = store.getState().page;
      const { settings: liveSettings, session_data: liveSessionData } = livePage || {};
      const {  data: dataDraft } = message.data || {};
      // FE-4577 — a socket frame is a DELIVERY like any other: it carries its
      // mark in `session_data.fds` (there are no headers on this transport) and
      // may carry the classes an inserted node needs. This path used to call the
      // parsers directly, bypassing the trust boundary, so it saw neither the
      // stored design system nor the mark.
      const data = normalizeResponseBody(dataDraft || message.data || {}, { ingest: true });
      const { page: pp, settings: settingsNew, variableData, screenId } = data.sections ? parseOldFormat(data) : parseNewFormat(data);
      
      // Handle new update mode scheme for WebSocket updates
      let finalPageData = pp;
      let finalVariableData = variableData;
      let shouldUpdateCache = false;
      
      if (data.mode === 'update') {
        const currentPageData = livePage?.data?.page;
        const currentVariableData = livePage?.variableData;
        
        const { page: updatedPage, variableData: updatedVariableData, shouldUpdateCache: cacheFlag } = 
          updateDataWithModeUpdate(currentPageData, data, currentVariableData);
        
        finalPageData = updatedPage;
        finalVariableData = updatedVariableData;
        shouldUpdateCache = cacheFlag;
      }
      
      if (settingsNew?.command) {
        const allVariables = merge({}, (liveSettings?.variables || {}), (livePage?.variableData || {}), finalVariableData || {}, getGlobalVariables() || {});
        const resolvedCommand = replacePlaceholdersWithVariables(settingsNew.command, allVariables) as string;
        const newCommand = resolvedCommand.replace(/^\//, '').replace(/\/$/, '');
        const currentPath = window.location.pathname.replace(/^\//, '').replace(/\/$/, '') + window.location.search;

        if (!/\{\{[^}]+\}\}/.test(newCommand) && newCommand !== currentPath) {
          if (window.location.pathname + window.location.search !== `/${newCommand}`) {
            window.history.pushState(null, '', `/${newCommand}`);
          }
        }
      }
      
      dispatch(pageSuccess({ 
        data: finalPageData, 
        settings: { ...liveSettings, ...settingsNew },
        session_data: {
          ...liveSessionData,
          hash: `skip-s-${v4uuid()}`,
        }, 
        variableData: finalVariableData,
        screenId,
       }));
       
      if (message.data?.requestData && message.data?.requestData.component_id && message.data?.requestData.screen_id) {
        const { command, action, component_id: componentId, screen_id: screenId } = message.data?.requestData;
        const cacheKey = formShimmerCacheKey({ command, action, component_id: componentId, screen_id: screenId });
        const { status, transformed } = transformShimmerCache(data?.screen?.sections);

        // Handle caching based on mode and settings
        if (data.mode === 'update') {
          // For update mode, save the final transformed data
          if (shouldUpdateCache) {
            setLocalCache(cacheKey, {
              ...data,
              screen: {
                ...data.screen,
                sections: finalPageData,
              },
              variable_data: finalVariableData,
            });
          }

        } else if (status && transformed) {
            setLocalCache(cacheKey, {
              ...data,
              screen: {
                ...data.screen,
                sections: transformed,
              },
              variable_data: variableData,
            });
          }
      }
    }
  }, [dispatch, store]);

  const connectWebSocket = useCallback(() => {
    if (!deviceId || !sessionId || socketRef.current) return;

    const provider = isProcesses ? 'native' : 'socket.io';
    // Create WebSocket provider using factory
    // You can change 'socket.io' to 'native' to use the Golang WebSocket service
    const wsProvider = WebSocketFactory.createProvider(provider, {
      deviceId,
      sessionId,
      publicUrl: config?.data?.public_url,
      onPageUpdate: handlePageUpdate,
      onError: (error) => {
        console.error('WebSocket Error:', error);
      },
    });

    wsProvider.connect();
    socketRef.current = wsProvider;

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [deviceId, sessionId, handlePageUpdate]);

  useEffect(() => {
    
    if (!isProcesses && deviceId && sessionId) {
      connectWebSocket();
    }
    // If live_updates = true and credentials are available
    else if (settings?.live_updates && deviceId && sessionId) {
      // Close old connection before establishing new one
      if (socketRef.current) {
        // skip as it's already connected
        return;
      }
      // Connect new WebSocket
      connectWebSocket();
    } 
    // If live_updates = false and WS is connected → disconnect
    else if (!settings?.live_updates && socketRef.current && deviceId && sessionId) {
      socketRef.current.setManualDisconnect(true); // Notify provider this is a manual disconnect
      socketRef.current.disconnect();
      socketRef.current = null;
    }
  }, [settings?.live_updates, deviceId, sessionId, connectWebSocket]);

  // useEffect(() => {
  //   if (document.fonts.check('1em Inter')) {
  //     setCustomFontLoaded(true);
  //   } else {
  //     document.fonts.ready.then(() => {
  //       if (document.fonts.check('1em Inter')) {
  //         setCustomFontLoaded(true);
  //       }
  //     });
  //   }
  // }, []);

  useEffect(() => {
    if (!pp) return;
    const url = analyzeAndPrefetchImages(pp);
    preloadImages(url);
  }, [pp]);

  // if (!fontsLoaded || !customFontLoaded) {
  //   return <div style={{ color: 'transparent', fontFamily: 'Inter', fontWeight: 600 }}>Loading <span style={{ color: 'transparent', fontFamily: 'Inter', fontWeight: 400 }}>...</span></div>;
  // }

  return (
    <div className={styles.page} style={generateComponentStyles(pageBackgroundStyle)}>
      <div
        ref={pageWrapRef}
        className={isColumnRoot ? `${styles.pageWrap} ${styles.pageWrapColumn}` : styles.pageWrap}
        style={wrapStyle}
      >
        {(typeof pp === 'object' && Object.keys(pp) && !Array.isArray(pp)) ? (
          <ContainerTemplate
            {...pp as NodeTypeScheme}
            pageSettings={newSettings}
            styleChunks={styleChunks}
            topLayerNode
            parentStyle={topLayerParentStyle}
            hash={hash}
            enableLocalShimmer={localShimmer}
          />
        ) : null}

        {Array.isArray(pp) ? (
          <>
            {pp.map((item) => {
              const Component = componentMapping[item.component];
              if (Component) {
                return <Component key={item.id} {...item} pageSettings={newSettings} styleChunks={styleChunks} topLayerNode parentStyle={topLayerParentStyle} enableLocalShimmer={localShimmer} />;
              } if (item.component === 'container' || item.component === 'table') {
                return <ContainerTemplate
                key={item.id}
                {...(item as NodeTypeScheme)}
                pageSettings={newSettings}
                styleChunks={styleChunks}
                topLayerNode
                parentStyle={topLayerParentStyle}
                hash={hash}
                enableLocalShimmer={localShimmer}
                />;
              } if (isUnknownComponent(item.component)) {
                return <UnknownComponent key={item.id} id={item.id} component={item.component} style={item.style} parentStyle={topLayerParentStyle} />;
              }
              return null;
            })}
          </>
        ) : null}
      </div>
    </div>
  );
};

export default Page;